import { useState, useEffect, useCallback } from 'react';
import service from "../../services/Service";
import { GetColumnLiteralSearchProps } from '../../utils/functions';
import DataTable from '../DataTable/DataTable';
import {
  CheckOutlined, CloseOutlined
} from "@ant-design/icons";
import Loading from "../../utils/loading";
import { withLocale } from '../../l10n';
import { notification } from 'antd';
import { formatDateTime, compareDates } from '../../utils/dates';

const BoardsInfoTable = ({ lang }) => {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: true,
  });

  const [data, setData] = useState({
    boardsInfo: [],
    comercial_model_filters: [],
    hardware_model_filters: [],
    daemon_version_filters: [],
    daemon_version_default_filters: []
  });

  // ===== 2. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));

  // ===== 3. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    updateUI('loading', true);
    try {
      const res = await service.get("boards/info");
      const comercial_models = Array.from(new Set(res.data.map(e => e.info?.comercial_model ?? null)));
      const hardware_models = Array.from(new Set(res.data.map(e => e.info?.board_model ?? null)));
      const daemon_versions = Array.from(new Set(res.data.map(e => e.info?.quiiotd_version ?? null)));

      setData({
        boardsInfo: res.data,
        comercial_model_filters: comercial_models.map((item) => ({ text: item ?? "Vacio", value: item })),
        hardware_model_filters: hardware_models.map((item) => ({ text: item ?? "Vacio", value: item })),
        daemon_version_filters: daemon_versions.map((item) => ({ text: item ?? "Vacio", value: item })),
        daemon_version_default_filters: daemon_versions.filter(item => item !== null),
      });
    } catch (error) {
      console.error("Error loading boards info:", error);
      notification["error"]({
        message: lang.handle("Error al obtener datos"),
      });
    } finally {
      updateUI('loading', false);
    }
  }, [lang]);

  const isUpdated = (record) => {
    const model_versions = {
      'v0.3-1-80384a9': ['A20', 'TRB245', 'RUT2XX', 'RUT2M', 'RUT9XX', 'RUT9M'],
      'v0.3-1-01cbbac': ['TRB145'],
      'v0.3-1-f99bf4b': ['RT5350']
    };
    return record.info?.quiiotd_version && model_versions[record.info.quiiotd_version]?.includes(record.info.board_model);
  };

  // ===== 4. EFFECTS =====
  useEffect(() => {
    Pfetch();
  }, [Pfetch]);

  // ===== 5. CONFIG (columnas, etc) =====
  const columns = [
    {
      title: lang.handle("Estado"),
      dataIndex: 'status',
      align: "center",
      render: (text, record) => (
        isUpdated(record) ? <CheckOutlined style={{ color: 'green' }} /> : <CloseOutlined style={{ color: 'red' }} />
      ),
      filters: [{ text: 'Actualizado', value: true }, { text: 'No actualizado', value: false }],
      onFilter: (value, record) => isUpdated(record) === value,
    },
    {
      title: lang.handle("UUID"),
      dataIndex: 'uuid',
      align: "center",
      ...GetColumnLiteralSearchProps('uuid', lang)
    },
    {
      title: lang.handle("Modelo"),
      dataIndex: ['info', 'comercial_model'],
      align: "center",
      filters: data.comercial_model_filters,
      onFilter: (value, record) => { const check = record.info?.comercial_model ?? null; return check === value },
    },
    {
      title: lang.handle("Modelo de hardware"),
      dataIndex: ['info', 'board_model'],
      align: "center",
      filters: data.hardware_model_filters,
      onFilter: (value, record) => { const check = record.info?.board_model ?? null; return check === value },
    },
    {
      title: lang.handle("Fecha de compilación"),
      dataIndex: ['info', 'compilation_date'],
      align: "center",
      sorter: (a, b) => new Date(a.info?.compilation_date) - new Date(b.info?.compilation_date),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: lang.handle("Versión de Daemon"),
      dataIndex: ['info', 'quiiotd_version'],
      align: "center",
      filters: data.daemon_version_filters,
      onFilter: (value, record) => { const check = record.info?.quiiotd_version ?? null; return check === value },
      defaultFilteredValue: data.daemon_version_default_filters
    },
    {
      title: lang.handle('Fecha de actualización'),
      dataIndex: 'update_ts',
      align: "center",
      render: (val) => <p> {formatDateTime(val)} </p>,
      sorter: (a, b) => compareDates(a.update_ts, b.update_ts),
      sortDirections: ["descend", "ascend"],
    }
  ];

  // ===== 6. RENDER =====
  return (
    <div className='card card-body'>
      {ui.loading && <Loading loading={ui.loading} />}
      {!ui.loading &&
        <DataTable
          columns={columns}
          dataSource={data.boardsInfo}
          rowKey="uuid"
          loading={ui.loading}
        />
      }
    </div>
  );
};

export default withLocale(BoardsInfoTable);
